/**
 * Pure helpers extracted from `MetroMapCanvas.tsx` for unit-test
 * reachability. The component itself owns WebGL2 / rAF / canvas
 * plumbing that jsdom can't run; the helpers below are dependency-free
 * so they can be exercised at full coverage in Vitest.
 *
 * Lives in a separate file (rather than as additional `export` slots
 * on MetroMapCanvas.tsx) to keep `react-refresh/only-export-components`
 * green — the lint rule wants component files to export ONLY components.
 */
import { elementVisibleInMode, type Mode, type SchemaData } from '../schema/types'
import type { StationInfo, StationLookup } from '../state/metroMapStore'
import type { Theme } from '../geometry/types'

export const MOBILE_BREAKPOINT_CSS_PX = 600
export const FOCUS_ZOOM_MOBILE = 3.0
export const FOCUS_ZOOM_DESKTOP = 1.5

export const CLEAR_LIGHT: readonly [number, number, number, number] = [1, 1, 1, 1]
export const CLEAR_DARK: readonly [number, number, number, number] = [
  0x1c / 0xff,
  0x1c / 0xff,
  0x1c / 0xff,
  1,
]

export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'

export interface FocusTargetArgs {
  readonly canvasX: number
  readonly canvasY: number
  readonly currentZoom: number
  readonly focusZoom: number
  readonly viewportPxW: number
  readonly viewportPxH: number
  readonly dpr: number
  /** TASK-253: 100 CSS px above viewport centre. Pass 0 for old centre-aligned behaviour. */
  readonly upwardOffsetCssPx: number
  /**
   * Cross-platform parity §5.1: width (in CSS pixels) of an opaque overlay
   * on the LEFT edge of the viewport that covers the GL surface — typically
   * the desktop `.expandedSidebar` (360 px panel + 16 px screen-edge inset
   * = 376 px) on tablet/desktop when the route-builder feature flag is on.
   *
   * Mirrors Android `FocusPanTarget.compute(leadingInsetPx = …)` and iOS's
   * implicit `UIScrollView.adjustedContentInset` (propagated automatically
   * from `.safeAreaInset(edge: .leading)` on the regular sidebar). When
   * non-zero, the focused station is centred in the *visible* horizontal
   * band (right of the sidebar) rather than at viewport mid-x where it
   * would land partially behind the panel.
   *
   * Default 0 keeps the legacy centre-aligned behaviour for mobile and for
   * any caller that doesn't render a leading overlay.
   */
  readonly leftInsetCssPx?: number
}

export interface FocusTarget {
  readonly zoom: number
  readonly panX: number
  readonly panY: number
}

/**
 * Pure formula for cameraAnimator setTarget when focusing on a station.
 *
 * Zoom: never zoom OUT — `max(currentZoom, focusZoom)`.
 * Pan: places the station at the *visible* viewport centre — the full
 * viewport minus any leading overlay (e.g. the desktop sidebar) — and
 * minus an upward offset in CSS pixels.
 *
 * The leading-inset shift mirrors Android `FocusPanTarget.compute`:
 *   panX = canvasX - (W + leadingInsetCanvasPx) / (2 * targetZoom)
 *
 * which expands to the previous formula plus `-(leadingInsetCanvasPx)/(2*z)`.
 * `leadingInsetCanvasPx = leftInsetCssPx * dpr` — same CSS→canvas-px
 * conversion as the upward offset.
 *
 * Adding `focusOffsetCanvasY` to panY moves the camera DOWN, which moves
 * the station UP on screen.
 */
export function computeFocusTarget(args: FocusTargetArgs): FocusTarget {
  const targetZoom = Math.max(args.currentZoom, args.focusZoom)
  const focusOffsetCanvasY = (args.upwardOffsetCssPx * args.dpr) / targetZoom
  const leftInsetCssPx = args.leftInsetCssPx ?? 0
  const leftInsetShiftCanvasX = (leftInsetCssPx * args.dpr) / (2 * targetZoom)
  return {
    zoom: targetZoom,
    panX: args.canvasX - args.viewportPxW / (2 * targetZoom) - leftInsetShiftCanvasX,
    panY: args.canvasY - args.viewportPxH / (2 * targetZoom) + focusOffsetCanvasY,
  }
}

export interface ViewportPicker {
  readonly innerWidth: number
  readonly innerHeight: number
  readonly clientWidth: number
  readonly clientHeight: number
  readonly screenWidth: number
  readonly screenHeight: number
  /**
   * `window.visualViewport?.height` — the authoritative VISIBLE viewport
   * height in CSS px, i.e. what the user can actually see inside Safari's
   * URL-bar overlay once it settles. When present it is used verbatim for
   * the canvas height so the canvas never OVERSHOOTS the visible area.
   *
   * Bug 7b (iPad Safari letterbox): the legacy full-screen heuristic sized
   * the height to `max(innerHeight, clientHeight, screenHeight)`, i.e. the
   * full PHYSICAL screen. On iPad Safari that is taller than the visible
   * Safari window (the URL-bar overlay eats the difference, and before
   * `visualViewport` settles the overshoot is worse), leaving a dark
   * letterbox strip below the map. `visualViewport.height` is the exact
   * visible height and eliminates the overshoot.
   *
   * `undefined` (non-Safari / engines without the `visualViewport` API)
   * falls back to the legacy heuristic, preserving the TASK-241 iPhone
   * status-bar workaround. On desktop `visualViewport.height ≈ innerHeight`
   * so passing it is a no-op there (no desktop / non-Safari regression).
   */
  readonly visualViewportHeight?: number
}

/**
 * Pick the viewport dimensions that body / canvas / #root should be
 * sized to, based on whether we're in iPhone full-screen Safari (where
 * the status-bar overlay shrinks innerHeight below the physical screen
 * and we need to take the max) or iPad stage manager / split view
 * (where the Safari window is smaller than the iPad screen, so taking
 * the max would oversize the canvas beyond the visible window).
 *
 * Heuristic: `innerWidth >= screen.width` means we ARE full-screen.
 */
export function pickViewportSize(args: ViewportPicker): { readonly w: number; readonly h: number } {
  const isFullScreen = args.innerWidth >= args.screenWidth
  const w = isFullScreen
    ? Math.max(args.innerWidth, args.clientWidth, args.screenWidth)
    : args.innerWidth
  // Legacy full-screen height heuristic — the max of every metric browsers
  // expose, sizing the canvas to the full physical screen so it bleeds
  // under iPhone's translucent status/URL chrome (TASK-241). Kept as the
  // fallback for engines without `visualViewport`.
  const legacyH = isFullScreen
    ? Math.max(args.innerHeight, args.clientHeight, args.screenHeight)
    : args.innerHeight
  // Bug 7b: the VISIBLE viewport height is authoritative when available.
  // `visualViewport.height` is the exact CSS-px height the user sees inside
  // Safari's URL-bar overlay once it settles — use it verbatim so the canvas
  // never overshoots into a dark letterbox on iPad Safari.
  const h = args.visualViewportHeight ?? legacyH
  return { w, h }
}

export function focusZoomForViewport(): number {
  /* v8 ignore next — SSR guard; metro-frontend is CSR-only (Vite SPA). */
  if (typeof window === 'undefined') return FOCUS_ZOOM_MOBILE
  return window.innerWidth < MOBILE_BREAKPOINT_CSS_PX ? FOCUS_ZOOM_MOBILE : FOCUS_ZOOM_DESKTOP
}

export function getSystemTheme(): Theme {
  /* v8 ignore next — SSR guard; metro-frontend is CSR-only (Vite SPA). */
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light'
}

export function clearColorFor(theme: Theme): readonly [number, number, number, number] {
  return theme === 'dark' ? CLEAR_DARK : CLEAR_LIGHT
}

/**
 * Build the `StationLookup` that `MapTapped` events carry. The reducer
 * uses this to resolve a hit-test winner into a SelectedStationSnapshot
 * (rows 2/5) or emit a row-6 analytics event (station id from tapgrid
 * not present in schema.stations). Returns empty lookup when the schema
 * hasn't loaded yet — the reducer then treats every tap as "empty area".
 */
export function buildStationLookup(schema: SchemaData | null, mode?: Mode): StationLookup {
  if (schema === null || schema.tapGrids === undefined) {
    return { tapGrids: [], stationsById: new Map() }
  }
  // Night mode places a stop where the late-night frame draws it (TASK-1136).
  const atNight = mode === 'night'
  const byId = new Map<string, StationInfo>()
  for (const s of schema.stations) {
    const point = atNight && s.night !== undefined ? s.night : s
    byId.set(s.id, {
      id: s.id,
      localName: s.localName,
      canvasX: point.x,
      canvasY: point.y,
      lineId: s.line.id,
    })
  }
  // Only the active mode's tap areas (TASK-1136): a night-only stop shares
  // its station's day tap area, so by day it must not win the hit — and a
  // station hidden at night must not shadow a night one.
  const visible =
    mode === undefined
      ? schema.tapGrids
      : schema.tapGrids.filter((g) => elementVisibleInMode(g.modes, mode))
  const tapGrids = atNight
    ? visible.map((g) => (g.night === undefined ? g : { ...g, ...g.night }))
    : visible
  return { tapGrids, stationsById: byId }
}

/**
 * Resolve bubble fill color from the schema, given a `lineId`. Android
 * uses the first station on that line's `line.hexColor`; frontend does
 * the same. Returns a gray fallback if the line can't be resolved
 * (schema drift or cold-start race).
 *
 * [theme] is currently unused — SchemaLine has only a single hexColor.
 * If the schema later grows per-theme color variants, switch on [theme]
 * here. Keep the signature accepting [theme] so the call site stays
 * stable across that future change.
 */
export function hexColorFromSchemaLineId(
  schema: SchemaData | null,
  lineId: string,
  _theme: Theme,
): string {
  if (schema === null) return '#888888'
  const station = schema.stations.find((s) => s.line.id === lineId)
  if (station === undefined) return '#888888'
  return station.line.hexColor
}

/**
 * Inputs for {@link computeRouteAutoFit} — bounding-box + viewport math
 * extracted from `MetroMapCanvas.tsx`'s auto-fit-to-route effect so it
 * can be unit-tested without canvas / rAF / DOM rect plumbing.
 *
 * `stations` is the filtered list of stations (canvas-space) that the
 * selected route covers. The caller is responsible for resolving the
 * route's leg endpoints against the schema and feeding the resulting
 * positions in here. `canvasWidth` / `canvasHeight` are PHYSICAL pixel
 * dimensions of the GL backing buffer (`canvas.width` / `canvas.height`,
 * not CSS px); `panelHeightCss` is the CSS-pixel height of the bottom-
 * occluded area (RouteCard mobile overlay or StationCard carousel).
 */
export interface RouteAutoFitArgs {
  readonly stations: ReadonlyArray<{ readonly x: number; readonly y: number }>
  /** Canvas physical-px width (`canvas.width`). */
  readonly canvasWidth: number
  /** Canvas physical-px height (`canvas.height`). */
  readonly canvasHeight: number
  /** Effective DPR — `devicePixelRatio * visualViewport.scale`. */
  readonly dpr: number
  /** CSS-pixel height of any bottom panel occluding the GL surface. */
  readonly panelHeightCss: number
  /**
   * CSS-pixel width of the iPad / desktop sidebar that occludes the
   * LEFT side of the canvas (RouteCard + StationDetails host).
   * Default 0 (phone — no sidebar). When >0, the bbox is fit into the
   * (canvasWidth − leftPanelWidthCss) visible area and re-centered so
   * the route sits at `leftPanel.right + visibleWidth / 2` rather than
   * the full canvas centerline (which would land behind the sidebar).
   */
  readonly leftPanelWidthCss?: number
  /**
   * Hard upper bound on the auto-fit zoom. Matches the host pan/zoom
   * subsystem's `maximumZoomScale` so the formula's `panX/panY` stay
   * consistent with the clamp applied downstream. Without this, a tiny
   * route (two adjacent stations) produces a `targetZoom > maxZoom`;
   * the scroll layer clamps the zoom but keeps the pan computed at the
   * unclamped zoom, which places the visible window thousands of canvas
   * units off the actual route — "zoom teleported me to the SE corner
   * of the map". Cross-platform default 4.0 mirrors iOS / Android
   * maxima.
   */
  readonly maxZoom?: number
}

export interface RouteAutoFit {
  readonly zoom: number
  readonly panX: number
  readonly panY: number
}

/**
 * Side / top / bottom CSS padding around the route bbox + the A/B
 * bubble height that extends UPWARD from the topmost station. Same
 * constants the original useEffect used inline; kept here so the
 * helper reproduces the math byte-for-byte.
 */
const ROUTE_AUTO_FIT_SIDE_PAD_CSS = 32
// Bumped 32 → 64 cross-platform 2026-05-05: at 32pt the A/B bubble
// apex landed too close to the system status bar / iOS Dynamic Island,
// clipping the bubble label on real devices. iOS, Android, and web
// share this constant for parity.
const ROUTE_AUTO_FIT_TOP_PAD_CSS = 64
// Bumped 32 → 96 web 2026-05-06: user feedback on tablet layout —
// short routes pinned the southernmost A/B station too close to the
// visible viewport bottom, leaving no breathing room below the bubble
// label. 96 gives ~1× bubble-height worth of clearance on top of the
// natural bubble extent. Phone uses the same constant; the bottom
// route panel already provides much larger occlusion there so this
// only kicks in when no panel is mounted.
const ROUTE_AUTO_FIT_BOTTOM_PAD_CSS = 96
const ROUTE_AUTO_FIT_BUBBLE_HEIGHT_CSS = 56
// The A/B pin is centred on its station (BubbleOverlay BUBBLE_WIDTH), so the
// side pad is measured from the pin's edge: half a bubble is added to it
// (TASK-986 — a route ending at the canvas' east edge left pin A half off
// screen). Cross-platform: iOS RouteAutoFitConstants.bubbleWidthCss.
const ROUTE_AUTO_FIT_BUBBLE_WIDTH_CSS = 50

/**
 * Pure formula for the camera target when auto-fitting to a selected
 * route. Returns null when `stations` is empty (caller should bail
 * out — there's nothing to fit). Otherwise returns `{ zoom, panX,
 * panY }` ready to feed into `cameraAnimator.setTarget`.
 *
 * - Horizontal: bbox is centred in the canvas width, with 32 css px
 *   side padding beyond half a bubble, so the end pins stay inside.
 * - Vertical: northernmost station (bbox.minY) sits at
 *   `(TOP_PAD + BUBBLE_HEIGHT) * dpr` pixels below the canvas top,
 *   so the A/B bubble apex clears the 32-px viewport gap.
 * - Zoom: smaller of the horizontal / vertical fit ratios — the bbox
 *   fits entirely inside the available area (canvas minus side pads
 *   horizontally; canvas minus panel + top + bubble + bottom pads
 *   vertically). Both `bboxW` / `bboxH` are clamped to a minimum of
 *   1 so a single-station route still produces a finite zoom.
 */
export function computeRouteAutoFit(args: RouteAutoFitArgs): RouteAutoFit | null {
  if (args.stations.length === 0) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const s of args.stations) {
    if (s.x < minX) minX = s.x
    if (s.y < minY) minY = s.y
    if (s.x > maxX) maxX = s.x
    if (s.y > maxY) maxY = s.y
  }
  const dpr = args.dpr
  const sidePadPx = (ROUTE_AUTO_FIT_SIDE_PAD_CSS + ROUTE_AUTO_FIT_BUBBLE_WIDTH_CSS / 2) * dpr
  const topPadPx = (ROUTE_AUTO_FIT_TOP_PAD_CSS + ROUTE_AUTO_FIT_BUBBLE_HEIGHT_CSS) * dpr
  const bottomPadPx = ROUTE_AUTO_FIT_BOTTOM_PAD_CSS * dpr
  const panelHeightPx = args.panelHeightCss * dpr
  const leftPanelPx = (args.leftPanelWidthCss ?? 0) * dpr
  const bboxW = Math.max(maxX - minX, 1)
  const bboxH = Math.max(maxY - minY, 1)
  const availW = Math.max(args.canvasWidth - leftPanelPx - 2 * sidePadPx, 1)
  const availH = Math.max(args.canvasHeight - panelHeightPx - topPadPx - bottomPadPx, 1)
  // Clamp to host's max zoom BEFORE computing pan — pan formulas depend
  // on `targetZoom`, so feeding the unclamped value makes panX/panY
  // consistent with a zoom the pan/zoom subsystem can't actually adopt.
  // Canonical failure: OXC → Bond Street (two Central-line neighbours)
  // wants `targetZoom ≈ 7.7`; the host clamps to 4.0 but applies pan at
  // 7.7, landing the visible window kilometres off the route.
  const maxZoom = args.maxZoom ?? 4
  const targetZoom = Math.min(Math.min(availW / bboxW, availH / bboxH), maxZoom)
  const bboxCx = (minX + maxX) / 2
  // Horizontal pan: target the bbox center at the VISIBLE region center
  // (right of the sidebar) instead of the full canvas centerline. Mirror
  // of iOS `_focusOnRoute`'s `adjustedContentInset.left` compensation
  // (TASK-273 2026-05-07 user fix). When `leftPanelWidthCss === 0` (phone
  // / no sidebar) this collapses to the previous full-canvas formula.
  const visibleCenterPx = leftPanelPx + (args.canvasWidth - leftPanelPx) / 2
  const targetPanX = bboxCx - visibleCenterPx / targetZoom
  // Vertical centring: when the horizontal fit wins the zoom is
  // smaller than the vertical-only fit ratio, so the bbox doesn't
  // fill the full available vertical band. Without redistribution
  // the bbox sticks to the topPad edge and the empty slack hangs
  // BELOW the route — visually a big gap between the southernmost
  // station and the bottom panel. Split the slack equally so the
  // bbox sits centred between the topPad and the panel/bottomPad.
  // (User report 2026-05-05: "паддинг снизу пропал".) Floor at zero
  // when vertical-fit wins so the formula degenerates back to the
  // bubble-clearance pin.
  const verticalSlack = Math.max(0, availH - bboxH * targetZoom)
  const targetPanY = minY - (topPadPx + verticalSlack / 2) / targetZoom
  return { zoom: targetZoom, panX: targetPanX, panY: targetPanY }
}

/**
 * Optional URL-params override for the initial map view. Used by visual
 * regression tests to position the map deterministically on a specific
 * element or area. If all three parameters are present and valid, the
 * default cover-fit initial view is skipped and these values are used
 * instead.
 *
 * URL shape: /?x=<centerX>&y=<centerY>&z=<zoomFactor>
 *   - x, y — world-space coordinates (MMTG coordinate system, 0..4096)
 *   - z    — zoom factor (pixels-per-world-unit, positive float)
 *
 * Intentionally silent on invalid input: a malformed param falls back to
 * cover-fit rather than showing an error, so accidental production URLs
 * with stale params still render something sensible.
 */
export function readInitialViewOverride(): { x: number; y: number; z: number } | null {
  /* v8 ignore next — SSR guard; metro-frontend is CSR-only (Vite SPA). */
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  const x = Number(params.get('x'))
  const y = Number(params.get('y'))
  const z = Number(params.get('z'))
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null
  if (z <= 0) return null
  return { x, y, z }
}
